/**
 * React component for building selection display.
 * Shows building information when a building is selected.
 */

import { useEffect, useState } from 'react';
import { useBuildingSelectionStore } from '@shared/stores/buildingSelectionStore';
import { BuildingType, getBuildingDefinition, isTowerType } from '@shared/constants/buildings';
import { UnitType, getUnitDefinition } from '@shared/constants/units';
import { useEconomyStore } from '@shared/stores/economyStore';
import UnitIcon from './UnitIcon';

interface BuildingSelectionDisplayProps {
  scene: unknown | null;
}

interface BuildingData {
  id: string;
  buildingType: BuildingType;
  health: number;
  maxHealth: number;
  armor: number;
  level: number;
  isConstructed: boolean;
  constructionProgress: number;
  // Tower-specific stats
  damage?: number;
  range?: number;
  // Mine-specific stats
  income?: number;
  // Training stats (for barracks)
  currentTraining?: UnitType | null;
  trainingProgress?: number;
  trainingQueue?: UnitType[];
  // Upgrade stats
  upgradeProgress?: number | null;
  // Tech research stats (for tech building)
  techResearchProgress?: number | null;
  techResearchType?: string | null;
  techResearchQueue?: string[];
  currentTechResearch?: string | null;
}

export default function BuildingSelectionDisplay({ scene }: BuildingSelectionDisplayProps) {
  const selectedBuildingId = useBuildingSelectionStore((state) => state.selectedBuildingId);
  const [building, setBuilding] = useState<BuildingData | null>(null);
  const { addCurrency } = useEconomyStore(); // Must be called before any early returns

  useEffect(() => {
    if (!scene || typeof scene !== 'object' || scene === null || !selectedBuildingId) {
      setBuilding(null);
      return;
    }

    const sceneWithMethods = scene as {
      getBuildingManager?: () => {
        getBuilding: (id: string) =>
          | {
              getId: () => string;
              getBuildingType: () => BuildingType;
              getHealth: () => number;
              getMaxHealth: () => number;
              getArmor: () => number;
              getLevel: () => number;
              isConstructed: () => boolean;
              getConstructionProgress: () => number;
              getTowerDamage: () => number;
              getTowerRange: () => number;
              getCurrentTraining: () => UnitType | null;
              getTrainingProgress: () => number;
              getTrainingQueue: () => UnitType[];
              cancelLastTraining: () => boolean;
              cancelTrainingAtIndex: (index: number) => boolean;
            }
          | undefined;
      } | null;
      getBuildingUpgradeSystem?: () => {
        getUpgradeProgress: (buildingId: string) => number | null;
        getTechResearchProgress: (researchType: string) => number | null;
        isResearching: (researchType: string) => boolean;
        getTechResearchQueue: () => string[];
        getCurrentTechResearch: () => string | null;
        cancelUpgrade: (buildingId: string) => boolean;
        cancelLastTechResearch: () => boolean;
        cancelTechResearchAtIndex: (index: number) => boolean;
        cancelCurrentTechResearch: () => boolean;
      } | null;
    };

    const buildingManager = sceneWithMethods.getBuildingManager?.();
    if (!buildingManager) {
      setBuilding(null);
      return;
    }

    // Update building data function
    const updateBuildingData = () => {
      const b = buildingManager.getBuilding(selectedBuildingId);
      if (b) {
        const buildingType = b.getBuildingType();
        const data: BuildingData = {
          id: b.getId(),
          buildingType,
          health: b.getHealth(),
          maxHealth: b.getMaxHealth(),
          armor: b.getArmor(),
          level: b.getLevel(),
          isConstructed: b.isConstructed(),
          constructionProgress: b.getConstructionProgress(),
        };

        // Add tower-specific stats
        if (isTowerType(buildingType)) {
          data.damage = b.getTowerDamage();
          data.range = b.getTowerRange();
        }

        // Add mine-specific stats
        if (buildingType === BuildingType.MINE && b.isConstructed()) {
          data.income = calculateMineIncome(b.getLevel());
        }

        // Add training stats (for barracks)
        if (buildingType === BuildingType.BARRACKS && b.isConstructed()) {
          data.currentTraining = b.getCurrentTraining();
          data.trainingProgress = b.getTrainingProgress();
          data.trainingQueue = b.getTrainingQueue();
        }

        // Add upgrade progress
        const upgradeSystem = sceneWithMethods.getBuildingUpgradeSystem?.();
        if (upgradeSystem) {
          data.upgradeProgress = upgradeSystem.getUpgradeProgress(selectedBuildingId);

          // Add tech research progress and queue (for tech building)
          if (buildingType === BuildingType.TECH_BUILDING) {
            data.techResearchQueue = upgradeSystem.getTechResearchQueue();
            data.currentTechResearch = upgradeSystem.getCurrentTechResearch();
            if (data.currentTechResearch) {
              data.techResearchProgress = upgradeSystem.getTechResearchProgress(
                data.currentTechResearch
              );
              data.techResearchType = data.currentTechResearch;
            }
          }
        }

        setBuilding(data);
      } else {
        setBuilding(null);
      }
    };

    // Initial update
    updateBuildingData();

    // Poll for updates (health, construction progress, etc.)
    const interval = setInterval(updateBuildingData, 100);

    return () => {
      clearInterval(interval);
    };
  }, [scene, selectedBuildingId]);

  if (!building) {
    return null;
  }

  const definition = getBuildingDefinition(building.buildingType);
  const isTower = isTowerType(building.buildingType);
  const isMine = building.buildingType === BuildingType.MINE;

  // Handler for canceling upgrade
  const handleCancelUpgrade = () => {
    if (!scene || typeof scene !== 'object' || scene === null || !selectedBuildingId) return;

    const sceneWithMethods = scene as {
      getBuildingUpgradeSystem?: () => {
        cancelUpgrade: (buildingId: string) => boolean;
      } | null;
    };

    const upgradeSystem = sceneWithMethods.getBuildingUpgradeSystem?.();
    if (upgradeSystem) {
      upgradeSystem.cancelUpgrade(selectedBuildingId);
    }
  };

  // Handler for canceling tech research at specific index
  const handleCancelTechResearchAtIndex = (index: number) => {
    if (!scene || typeof scene !== 'object' || scene === null) return;

    const sceneWithMethods = scene as {
      getBuildingUpgradeSystem?: () => {
        cancelTechResearchAtIndex: (index: number) => boolean;
      } | null;
    };

    const upgradeSystem = sceneWithMethods.getBuildingUpgradeSystem?.();
    if (upgradeSystem) {
      upgradeSystem.cancelTechResearchAtIndex(index);
    }
  };

  // Handler for canceling last tech research
  const handleCancelLastTechResearch = () => {
    if (!scene || typeof scene !== 'object' || scene === null) return;

    const sceneWithMethods = scene as {
      getBuildingUpgradeSystem?: () => {
        cancelLastTechResearch: () => boolean;
        cancelCurrentTechResearch: () => boolean;
        getTechResearchQueue: () => string[];
        getCurrentTechResearch: () => string | null;
      } | null;
    };

    const upgradeSystem = sceneWithMethods.getBuildingUpgradeSystem?.();
    if (upgradeSystem) {
      const queue = upgradeSystem.getTechResearchQueue();
      const current = upgradeSystem.getCurrentTechResearch();
      if (queue.length === 0 && current !== null) {
        upgradeSystem.cancelCurrentTechResearch();
      } else {
        upgradeSystem.cancelLastTechResearch();
      }
    }
  };

  // Handler for canceling training at specific index (for barracks queue icons)
  const handleCancelAtIndex = (index: number) => {
    if (!scene || typeof scene !== 'object' || scene === null || !selectedBuildingId) return;

    const sceneWithMethods = scene as {
      getBuildingManager?: () => {
        getBuilding: (id: string) =>
          | {
              cancelTrainingAtIndex: (index: number) => boolean;
              getTrainingQueue: () => UnitType[];
            }
          | undefined;
      } | null;
    };

    const buildingManager = sceneWithMethods.getBuildingManager?.();
    if (!buildingManager) return;

    const b = buildingManager.getBuilding(selectedBuildingId);
    if (!b) return;

    const queue = b.getTrainingQueue();
    if (index >= 0 && index < queue.length) {
      const unitType = queue[index];
      const cost = unitType === UnitType.MELEE ? 50 : 75; // SWORDSMAN_COST : ARCHER_COST
      const canceled = b.cancelTrainingAtIndex(index);
      if (canceled) {
        // Refund the currency
        addCurrency(cost);
      }
    }
  };

  // Handler for canceling last training (or currently training unit if it's the only one)
  const handleCancelLast = () => {
    if (!scene || typeof scene !== 'object' || scene === null || !selectedBuildingId) return;

    const sceneWithMethods = scene as {
      getBuildingManager?: () => {
        getBuilding: (id: string) =>
          | {
              cancelLastTraining: () => boolean;
              cancelCurrentTraining: () => UnitType | null;
              getTrainingQueue: () => UnitType[];
              getCurrentTraining: () => UnitType | null;
            }
          | undefined;
      } | null;
    };

    const buildingManager = sceneWithMethods.getBuildingManager?.();
    if (!buildingManager) return;

    const b = buildingManager.getBuilding(selectedBuildingId);
    if (!b) return;

    const queue = b.getTrainingQueue();
    const currentTraining = b.getCurrentTraining();

    // If there are items in the queue, cancel the last one
    if (queue.length > 0) {
      const lastUnit = queue[queue.length - 1];
      const cost = lastUnit === UnitType.MELEE ? 50 : 75; // SWORDSMAN_COST : ARCHER_COST
      const canceled = b.cancelLastTraining();
      if (canceled) {
        // Refund the currency
        addCurrency(cost);
      }
    }
    // If queue is empty but there's a currently training unit, cancel it
    else if (currentTraining !== null && b.cancelCurrentTraining) {
      const canceledUnit = b.cancelCurrentTraining();
      if (canceledUnit !== null) {
        const cost = canceledUnit === UnitType.MELEE ? 50 : 75; // SWORDSMAN_COST : ARCHER_COST
        // Refund the currency
        addCurrency(cost);
      }
    }
  };

  // Get next 6 units in queue for display (for barracks)
  // Include the currently training unit as the first icon if it exists
  const trainingQueue = building.trainingQueue || [];
  const currentTraining = building.currentTraining;
  const next6Units: (UnitType | null)[] = [];

  // Add currently training unit as first icon if it exists
  if (currentTraining !== null && currentTraining !== undefined) {
    next6Units.push(currentTraining);
  }

  // Add remaining units from queue (up to 6 total)
  const remainingSlots = 6 - next6Units.length;
  next6Units.push(...trainingQueue.slice(0, remainingSlots));

  return (
    <div className="flex flex-col gap-2" data-building-selection>
      <div className="flex items-start gap-3">
        {/* Building icon placeholder */}
        <div
          className="w-12 h-12 rounded bg-gray-700 flex items-center justify-center text-2xl flex-shrink-0"
          style={{ backgroundColor: getBuildingColor(building.buildingType) }}
        >
          {getBuildingIcon(building.buildingType)}
        </div>
        <div className="flex-1 flex flex-col gap-2">
          {/* Name, level, health, and stats in one row */}
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="text-white font-semibold">
                {definition.name}
                {building.isConstructed && (
                  <span className="text-yellow-400 ml-2">
                    Lv.{building.level}
                    {isMine && building.income !== undefined && (
                      <span className="text-green-400"> ({building.income} income)</span>
                    )}
                  </span>
                )}
              </div>
              {building.isConstructed ? (
                <div className="text-gray-300 text-sm">
                  Health: {building.health}/{building.maxHealth}
                </div>
              ) : (
                <div className="text-yellow-400 text-sm">
                  Building... {Math.floor(building.constructionProgress * 100)}%
                </div>
              )}
            </div>
            {/* Stats to the right */}
            {building.isConstructed && (
              <div className="flex gap-4 text-sm text-gray-300 flex-wrap">
                <div>Armor: {building.armor}</div>
                {isTower && building.damage !== undefined && (
                  <>
                    <div>Damage: {building.damage}</div>
                    <div>Range: {building.range}</div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Health bar and training section - with cancel button outside stats column */}
      <div className="flex items-start gap-3">
        {/* Building icon spacer - matches the icon width from above */}
        <div className="w-12 flex-shrink-0"></div>
        {/* Left section: Health bar, progress bar, and queue icons - fixed width based on 6 icons */}
        <div
          className="flex flex-col gap-2 flex-shrink-0"
          style={{ width: 'calc(3rem * 6 + 0.25rem * 5)' }}
        >
          {/* Health bar */}
          <div className="w-full h-2 bg-gray-700 rounded overflow-hidden">
            <div
              className={`h-full transition-all ${building.isConstructed ? 'bg-green-500' : 'bg-yellow-500'}`}
              style={{
                width: `${building.isConstructed ? (building.health / building.maxHealth) * 100 : building.constructionProgress * 100}%`,
              }}
            />
          </div>

          {/* Upgrade progress bar (for all buildings) */}
          {building.isConstructed &&
            building.upgradeProgress !== null &&
            building.upgradeProgress !== undefined && (
              <div className="flex flex-col gap-1">
                <div className="text-sm text-gray-300">
                  Upgrading...
                  <span className="ml-2 text-yellow-400">
                    {Math.floor(building.upgradeProgress * 100)}%
                  </span>
                </div>
                <div className="w-full h-2 bg-gray-700 rounded overflow-hidden">
                  <div
                    className="h-full bg-purple-500 transition-all"
                    style={{
                      width: `${building.upgradeProgress * 100}%`,
                    }}
                  />
                </div>
              </div>
            )}

          {/* Tech research progress bar and queue (for tech building) */}
          {building.isConstructed &&
            building.buildingType === BuildingType.TECH_BUILDING &&
            ((building.currentTechResearch !== null &&
              building.techResearchProgress !== null &&
              building.techResearchProgress !== undefined) ||
              (building.techResearchQueue && building.techResearchQueue.length > 0)) && (
              <>
                {/* Research progress bar */}
                {building.currentTechResearch !== null &&
                  building.techResearchProgress !== null &&
                  building.techResearchProgress !== undefined && (
                    <div className="flex flex-col gap-1">
                      <div className="text-sm text-gray-300">
                        Researching:{' '}
                        {building.currentTechResearch === 'tower_level_5'
                          ? 'Tower Lv5'
                          : building.currentTechResearch === 'armor_upgrade'
                            ? 'Armor +1'
                            : building.currentTechResearch === 'damage_upgrade'
                              ? 'Damage +10'
                              : building.currentTechResearch}
                        <span className="ml-2 text-yellow-400">
                          {Math.floor(building.techResearchProgress * 100)}%
                        </span>
                      </div>
                      <div className="w-full h-2 bg-gray-700 rounded overflow-hidden">
                        <div
                          className="h-full bg-cyan-500 transition-all"
                          style={{
                            width: `${building.techResearchProgress * 100}%`,
                          }}
                        />
                      </div>
                    </div>
                  )}
                {/* Research queue icons */}
                {(building.techResearchQueue && building.techResearchQueue.length > 0) ||
                building.currentTechResearch !== null ? (
                  <div className="flex gap-1 w-full">
                    {/* Show current research as first icon if exists */}
                    {building.currentTechResearch !== null && (
                      <div
                        className="flex-1 cursor-pointer hover:opacity-70 transition-opacity"
                        onClick={handleCancelLastTechResearch}
                        title={`Click to cancel currently researching: ${building.currentTechResearch === 'tower_level_5' ? 'Tower Lv5' : building.currentTechResearch === 'armor_upgrade' ? 'Armor +1' : building.currentTechResearch === 'damage_upgrade' ? 'Damage +10' : building.currentTechResearch || ''}`}
                      >
                        <div className="flex justify-center">
                          <div className="w-12 h-12 bg-gray-600 rounded border border-gray-500 flex items-center justify-center text-xs text-white">
                            {building.currentTechResearch === 'tower_level_5'
                              ? 'T5'
                              : building.currentTechResearch === 'armor_upgrade'
                                ? 'A+1'
                                : building.currentTechResearch === 'damage_upgrade'
                                  ? 'D+10'
                                  : (building.currentTechResearch || '').charAt(0).toUpperCase()}
                          </div>
                        </div>
                      </div>
                    )}
                    {/* Show next 5 items in queue */}
                    {building.techResearchQueue &&
                      building.techResearchQueue
                        .slice(
                          0,
                          building.currentTechResearch !== null &&
                            building.currentTechResearch !== undefined
                            ? 5
                            : 6
                        )
                        .map((researchType, index) => {
                          const queueIndex =
                            building.currentTechResearch !== null &&
                            building.currentTechResearch !== undefined
                              ? index
                              : index;
                          return (
                            <div
                              key={index}
                              className="flex-1 cursor-pointer hover:opacity-70 transition-opacity"
                              onClick={() => handleCancelTechResearchAtIndex(queueIndex)}
                              title={`Click to cancel ${researchType === 'tower_level_5' ? 'Tower Lv5' : researchType === 'armor_upgrade' ? 'Armor +1' : researchType === 'damage_upgrade' ? 'Damage +10' : researchType} (position ${queueIndex + 1} in queue)`}
                            >
                              <div className="flex justify-center">
                                <div className="w-12 h-12 bg-gray-600 rounded border border-gray-500 flex items-center justify-center text-xs text-white">
                                  {researchType === 'tower_level_5'
                                    ? 'T5'
                                    : researchType === 'armor_upgrade'
                                      ? 'A+1'
                                      : researchType === 'damage_upgrade'
                                        ? 'D+10'
                                        : researchType.charAt(0).toUpperCase()}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    {/* Fill remaining slots if less than 6 */}
                    {Array.from({
                      length: Math.max(
                        0,
                        6 -
                          (building.currentTechResearch !== null ? 1 : 0) -
                          (building.techResearchQueue?.length || 0)
                      ),
                    }).map((_, i) => (
                      <div key={`empty-${i}`} className="flex-1" />
                    ))}
                  </div>
                ) : null}
              </>
            )}

          {/* Training progress bar and queue icons */}
          {building.isConstructed &&
            building.buildingType === BuildingType.BARRACKS &&
            ((building.currentTraining !== null && building.trainingProgress !== undefined) ||
              next6Units.length > 0 ||
              currentTraining !== null ||
              trainingQueue.length > 0) && (
              <>
                {/* Training progress bar */}
                {building.currentTraining !== null && building.trainingProgress !== undefined && (
                  <div className="flex flex-col gap-1">
                    <div className="text-sm text-gray-300">
                      Training:{' '}
                      {building.currentTraining === UnitType.MELEE ? 'Swordsman' : 'Archer'}
                      <span className="ml-2 text-yellow-400">
                        {Math.floor(building.trainingProgress * 100)}%
                      </span>
                    </div>
                    <div className="w-full h-2 bg-gray-700 rounded overflow-hidden">
                      <div
                        className="h-full bg-blue-500 transition-all"
                        style={{
                          width: `${building.trainingProgress * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                )}
                {/* Queue icons - beneath progress bar */}
                {(next6Units.length > 0 ||
                  currentTraining !== null ||
                  trainingQueue.length > 0) && (
                  <div className="flex gap-1 w-full">
                    {next6Units.map((unitType, index) => {
                      if (unitType === null) {
                        // Fill empty slots to maintain 6-icon layout
                        return <div key={index} className="flex-1" />;
                      }

                      const unitDefinition = getUnitDefinition(unitType);
                      const isCurrentlyTraining =
                        index === 0 && currentTraining !== null && currentTraining === unitType;
                      // Calculate index in actual queue (skip current training if it's the first icon)
                      const queueIndex = isCurrentlyTraining
                        ? -1
                        : currentTraining !== null
                          ? index - 1
                          : index;

                      return (
                        <div
                          key={index}
                          className={`flex-1 cursor-pointer hover:opacity-70 transition-opacity ${isCurrentlyTraining ? 'opacity-100' : ''}`}
                          onClick={() => {
                            if (queueIndex >= 0) {
                              handleCancelAtIndex(queueIndex);
                            }
                          }}
                          title={
                            isCurrentlyTraining
                              ? `Currently training: ${unitDefinition.name}`
                              : `Click to cancel ${unitDefinition.name} (position ${queueIndex + 1} in queue)`
                          }
                        >
                          <div className="flex justify-center">
                            <UnitIcon
                              unitType={unitType}
                              health={unitDefinition.stats.health}
                              maxHealth={unitDefinition.stats.health}
                              size="small"
                              clickable={!isCurrentlyTraining}
                            />
                          </div>
                        </div>
                      );
                    })}
                    {/* Fill remaining slots if less than 6 */}
                    {Array.from({ length: Math.max(0, 6 - next6Units.length) }).map((_, i) => (
                      <div key={`empty-${i}`} className="flex-1" />
                    ))}
                  </div>
                )}
              </>
            )}
        </div>
        {/* Cancel button for upgrades - outside stats column, to the right, aligned to bottom */}
        {building.isConstructed &&
          building.upgradeProgress !== null &&
          building.upgradeProgress !== undefined && (
            <div
              className="w-12 h-12 cursor-pointer hover:opacity-70 transition-opacity flex items-center justify-center flex-shrink-0 self-end"
              onClick={handleCancelUpgrade}
              title="Cancel upgrade and refund cost"
            >
              {/* Grey background and border matching queue icons, with red circle and line */}
              <div className="w-full h-full bg-gray-600 rounded border border-gray-500 flex items-center justify-center relative">
                <div className="absolute inset-2 rounded-full border-4 border-red-700"></div>
                <div className="absolute inset-2 flex items-center justify-center">
                  <div className="w-6 h-1 bg-red-700 rotate-45 transform origin-center"></div>
                </div>
              </div>
            </div>
          )}

        {/* Cancel button for tech research - outside stats column, to the right, aligned to bottom */}
        {building.isConstructed &&
          building.buildingType === BuildingType.TECH_BUILDING &&
          ((building.currentTechResearch !== null &&
            building.techResearchProgress !== null &&
            building.techResearchProgress !== undefined) ||
            (building.techResearchQueue && building.techResearchQueue.length > 0)) && (
            <div
              className="w-12 h-12 cursor-pointer hover:opacity-70 transition-opacity flex items-center justify-center flex-shrink-0 self-end"
              onClick={handleCancelLastTechResearch}
              title={
                building.techResearchQueue &&
                building.techResearchQueue.length === 0 &&
                building.currentTechResearch !== null
                  ? 'Cancel currently researching'
                  : 'Cancel last research in queue'
              }
            >
              {/* Grey background and border matching queue icons, with red circle and line */}
              <div className="w-full h-full bg-gray-600 rounded border border-gray-500 flex items-center justify-center relative">
                <div className="absolute inset-2 rounded-full border-4 border-red-700"></div>
                <div className="absolute inset-2 flex items-center justify-center">
                  <div className="w-6 h-1 bg-red-700 rotate-45 transform origin-center"></div>
                </div>
              </div>
            </div>
          )}

        {/* Cancel button for barracks - outside stats column, to the right, aligned to bottom */}
        {building.isConstructed &&
          building.buildingType === BuildingType.BARRACKS &&
          ((building.currentTraining !== null && building.trainingProgress !== undefined) ||
            next6Units.length > 0 ||
            currentTraining !== null ||
            trainingQueue.length > 0) && (
            <div
              className="w-12 h-12 cursor-pointer hover:opacity-70 transition-opacity flex items-center justify-center flex-shrink-0 self-end"
              onClick={handleCancelLast}
              title={
                trainingQueue.length === 0 && currentTraining !== null
                  ? 'Cancel currently training unit'
                  : 'Cancel last unit in queue'
              }
            >
              {/* Grey background and border matching queue icons, with red circle and line */}
              <div className="w-full h-full bg-gray-600 rounded border border-gray-500 flex items-center justify-center relative">
                <div className="absolute inset-2 rounded-full border-4 border-red-700"></div>
                <div className="absolute inset-2 flex items-center justify-center">
                  <div className="w-6 h-1 bg-red-700 rotate-45 transform origin-center"></div>
                </div>
              </div>
            </div>
          )}
      </div>
    </div>
  );
}

/**
 * Calculate mine income based on level.
 * Uses the same formula as MiningIncomeSystem.
 * Income doubles each level: 2^(level-1) = 1, 2, 4, 8, 16...
 */
function calculateMineIncome(level: number): number {
  return Math.pow(2, level - 1);
}

/**
 * Get color for building type (for the icon background).
 */
function getBuildingColor(buildingType: BuildingType): string {
  const colors: Record<BuildingType, string> = {
    [BuildingType.TOWER_ARCHER]: '#3b82f6', // Blue
    [BuildingType.TOWER_CANNON]: '#f97316', // Orange
    [BuildingType.TOWER_MAGIC]: '#a855f7', // Purple
    [BuildingType.WALL]: '#6b7280', // Gray
    [BuildingType.BARRACKS]: '#ef4444', // Red
    [BuildingType.TECH_BUILDING]: '#06b6d4', // Cyan
    [BuildingType.HERO_SPAWNER]: '#eab308', // Yellow
    [BuildingType.MINE]: '#fbbf24', // Gold
  };
  return colors[buildingType] || '#4b5563';
}

/**
 * Get icon for building type.
 */
function getBuildingIcon(buildingType: BuildingType): string {
  const icons: Record<BuildingType, string> = {
    [BuildingType.TOWER_ARCHER]: '🏹',
    [BuildingType.TOWER_CANNON]: '💣',
    [BuildingType.TOWER_MAGIC]: '✨',
    [BuildingType.WALL]: '🧱',
    [BuildingType.BARRACKS]: '⚔️',
    [BuildingType.TECH_BUILDING]: '🔬',
    [BuildingType.HERO_SPAWNER]: '👑',
    [BuildingType.MINE]: '⛏️',
  };
  return icons[buildingType] || '🏠';
}
